import React, { Component } from 'react';
import PropTypes from 'prop-types';
import VideoHeader from 'components/VideoHeader';
import classNames from 'classnames';
import _ from 'lodash';
import { isMobileBreakpoint } from 'helpers/BreakpointHelper';
import { isShowcaseCountdown } from 'helpers/ContentHelper';
import { COLLECTION_HERO_IMAGE, getDynamicImage } from 'helpers/ImageHelper';
import { heroCarouselEnabled } from 'helpers/ConfigHelper';
import { noop } from 'helpers/GlobalHelper';
import Style from 'app/themes/styleguide/nation/HomeSlideHeader.module.scss';
import videoHeaderStyleV2 from './VideoHeader.v2.module.scss';
import singleVideoHeaderStyleV2 from './VideoHeaderSingle.v2.module.scss';
import InfoTags from './Components/InfoTags';
import SlideStyle from './HomeSlideHeader.module.scss';

/**
 * HomeSlideHeader Component
 * @param {Object}   panel        - Panel object
 * @returns {Component}           - React Component
    <HomeSlideHeader item={item} />
 */
class HomeSlideHeader extends Component {
  constructor(props) {
    super(props);
    const {
      item: { id },
      idx,
    } = props;
    this.state = {
      paused: false,
      timerActive: props.item?.eventData?.hasCountdown && isShowcaseCountdown(props.item),
      isDescriptionOpen: false,
      headerImageState: null,
    };
    this.mainPostition = null;
    this.altPostition = null;

    this.toggleDescription = this.toggleDescription.bind(this);
    this.slideId = _.uniqueId(`${id}_${idx}_slide_`);
  }

  componentDidUpdate(prevProps) {
    const { disableSingleVideoHeroRender, videoIsLive, item } = this.props;
    const {
      disableSingleVideoHeroRender: prevDisableSingleVideoHeroRender,
      item: prevItem,
    } = prevProps;
    if (
      disableSingleVideoHeroRender !== prevDisableSingleVideoHeroRender &&
      disableSingleVideoHeroRender
    ) {
      this.setState({
        headerImageState: videoIsLive ? 'live video' : 'autoplay video',
      });
    }
    if (prevItem?.eventData && item?.eventData !== prevItem.eventData) {
      if (item?.eventData?.hasCountdown && isShowcaseCountdown(item)) {
        this.setState({ timerActive: true });
      } else {
        this.setState({ timerActive: false });
      }
    }
  }

  setHeaderImageState = state => {
    this.setState({ headerImageState: state });
  };

  deactivateTimer = () => {
    this.setState({ timerActive: false });
  };

  toggleDescription(e) {
    e.stopPropagation();
    e.preventDefault();
    const { isDescriptionOpen } = this.state;
    this.setState({ isDescriptionOpen: !isDescriptionOpen });
  }

  renderHeaderComponent({ render, showHeaderBelowTags }) {
    const { foxAwesomeLayout, heroImage } = this.props;

    if (!render) return null;

    const testTag = showHeaderBelowTags
      ? 'first-collection-header-below-tags'
      : 'first-collection-header-above-tags';

    return (
      <div data-test={testTag}>
        {foxAwesomeLayout && this.renderSingleVideoHero()}
        {heroImage}
      </div>
    );
  }

  renderSingleVideoHero() {
    const {
      item,
      setVideoHero,
      removeVideoHero,
      idx,
      play,
      onSelection,
      foxAwesomeLayout,
      breakpoint,
      disableSingleVideoHeroRender,
      disableVideoRender,
    } = this.props;
    const {
      videoId,
      eventData,
      stillBackgroundImage,
      videoBackgroundImage,
      promoBackgroundImage: promoBackgroundImageItem,
      promoBackgroundVideo,
      watchLink,
      originalVideo,
    } = item;
    const { timerActive } = this.state;
    const promoBackgroundImage =
      timerActive && isShowcaseCountdown(item)
        ? eventData?.keyArt?.seriesDetail
        : promoBackgroundImageItem;
    const disableVideoForMobile = isMobileBreakpoint(breakpoint);
    const disableVideoForPromo = !!promoBackgroundImage;
    const dontRenderVideo = disableVideoForMobile || disableVideoRender || disableVideoForPromo;
    if (disableSingleVideoHeroRender) return null;
    const promoTypeIsShowcaseBurntIn = _.get(originalVideo, 'promoType') === 'showcaseBurntIn';
    return (
      <VideoHeader
        theme={videoHeaderStyleV2}
        foxAwesomeLayout={foxAwesomeLayout}
        id={videoId}
        idx={idx}
        image={getDynamicImage({
          breakpoint,
          url: disableVideoForPromo ? promoBackgroundImage : stillBackgroundImage,
          context: COLLECTION_HERO_IMAGE,
        })}
        onMount={setVideoHero}
        onTitleClick={onSelection}
        onUnmount={removeVideoHero}
        play={play}
        setHeaderImageState={this.setHeaderImageState}
        shadowOnPlayButton={false}
        themeSingleVideoHeader={singleVideoHeaderStyleV2}
        video={dontRenderVideo ? null : promoBackgroundVideo || videoBackgroundImage}
        watchLink={watchLink}
        promoTypeIsShowcaseBurntIn={promoTypeIsShowcaseBurntIn}
      />
    );
  }

  render() {
    const {
      account,
      allowInfoToggle,
      breakpoint,
      callCollectionImpressionEvent,
      collectionInfo,
      foxAwesomeLayout,
      hasSingleSlide,
      idx,
      isLive,
      item,
      onPrimaryButtonClick,
      onTitleClick,
      onWatch,
      overlay,
      overridePlayButtonClick,
      overridePlayButtonLink,
      router,
      saveLiveRestart,
      saveLiveVideo,
      setOverlay,
      setSuggestedChannel,
      showLiveButtons,
      smallCollectionHeader,
      title,
      useSeriesLogo,
    } = this.props;
    const { paused, isDescriptionOpen, headerImageState, timerActive } = this.state;
    const infoTagProps = {
      account,
      allowInfoToggle,
      breakpoint,
      callCollectionImpressionEvent,
      collectionInfo,
      foxAwesomeLayout,
      headerImageState,
      idx,
      isLive,
      isDescriptionOpen,
      item,
      onPrimaryButtonClick,
      onTitleClick,
      onWatch,
      overlay,
      overridePlayButtonClick,
      overridePlayButtonLink,
      router,
      saveLiveRestart,
      saveLiveVideo,
      setOverlay,
      setSuggestedChannel,
      deactivateTimer: this.deactivateTimer,
      showLiveButtons,
      smallCollectionHeader,
      style: Style,
      timerActive,
      useSeriesLogo,
      toggleDescription: this.toggleDescription,
    };
    const containerClasses = classNames([
      foxAwesomeLayout ? Style.fowAwesomeContainer : Style.container,
      paused ? Style.paused : Style.playing,
      SlideStyle.pageMaxWidth,
      hasSingleSlide ? Style.singleSlide : null,
      heroCarouselEnabled() ? [Style.carouselStyle, SlideStyle.carouselMaxWidth] : null,
      SlideStyle.showSlide,
    ]);
    const showHeaderBelowTags = foxAwesomeLayout && smallCollectionHeader;
    return (
      <div className={containerClasses} data-name={title} id={this.slideId}>
        {this.renderHeaderComponent({ render: !showHeaderBelowTags, showHeaderBelowTags })}
        <InfoTags {...infoTagProps} />
        {this.renderHeaderComponent({ render: showHeaderBelowTags, showHeaderBelowTags })}
      </div>
    );
  }
}

HomeSlideHeader.propTypes = {
  account: PropTypes.shape({}),
  allowInfoToggle: PropTypes.bool,
  breakpoint: PropTypes.string,
  collectionInfo: PropTypes.shape({}),
  coordinates: PropTypes.shape({}),
  disableSingleVideoHeroRender: PropTypes.bool,
  disableVideoRender: PropTypes.bool,
  foxAwesomeLayout: PropTypes.bool,
  hasSingleSlide: PropTypes.bool,
  setCoordinates: PropTypes.func,
  idx: PropTypes.number,
  isLive: PropTypes.bool,
  item: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  onPrimaryButtonClick: PropTypes.func,
  onSelection: PropTypes.func,
  onTitleClick: PropTypes.func.isRequired,
  onWatch: PropTypes.func,
  overlay: PropTypes.string,
  overridePlayButtonClick: PropTypes.func,
  overridePlayButtonLink: PropTypes.bool,
  play: PropTypes.bool,
  removeVideoHero: PropTypes.func,
  heroImage: PropTypes.shape({}),
  router: PropTypes.shape({}).isRequired,
  saveLiveRestart: PropTypes.func.isRequired,
  saveLiveVideo: PropTypes.func,
  setOverlay: PropTypes.func,
  setSuggestedChannel: PropTypes.func.isRequired,
  setVideoHero: PropTypes.func,
  showLiveButtons: PropTypes.bool,
  smallCollectionHeader: PropTypes.bool,
  title: PropTypes.string,
  toggleVideoModal: PropTypes.func,
  useSeriesLogo: PropTypes.bool,
  callCollectionImpressionEvent: PropTypes.func,
  videoIsLive: PropTypes.bool.isRequired,
};

HomeSlideHeader.defaultProps = {
  account: null,
  allowInfoToggle: false,
  breakpoint: null,
  callCollectionImpressionEvent: noop,
  collectionInfo: {},
  coordinates: null,
  disableSingleVideoHeroRender: null,
  disableVideoRender: null,
  foxAwesomeLayout: false,
  hasSingleSlide: false,
  idx: null,
  isLive: false,
  onPrimaryButtonClick: noop,
  onSelection: noop,
  onWatch: noop,
  overlay: null,
  overridePlayButtonClick: () => {},
  overridePlayButtonLink: false,
  play: false,
  removeVideoHero: noop,
  heroImage: null,
  saveLiveVideo: noop,
  setCoordinates: noop,
  setOverlay: noop,
  setVideoHero: noop,
  showLiveButtons: true,
  smallCollectionHeader: false,
  title: '',
  toggleVideoModal: noop,
  useSeriesLogo: false,
};

export default HomeSlideHeader;
